import type { FormikValues } from 'formik';
import * as Yup from 'yup';
import classNames from 'classnames';
import type { IFormProps } from '$components/composite/Form';
import Form from '$components/composite/Form';
import FormItem from '$components/composite/Form/FormItem';
import Input from '$components/composite/Form/Input';
import { SimpleLink } from '@/components/atoms/SimpleLink';
import { Styled } from './styled';

export interface Credentials {
  readonly email: string;
}

export interface ForgotPasswordFormProps {
  readonly values: Partial<Credentials>;
  readonly onSubmit: (values: FormikValues) => void;
  readonly onBackStep: () => void;
  readonly formContext: IFormProps['formContext'];
  readonly backStepLabel: string;
  readonly label: string;
  readonly placeholder: string;
  readonly instructions: string;
  readonly className?: string;
}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .trim()
    .email("That email address doesn't look right")
    .max(320, 'Email must be below 320 characters')
    .required('Please enter your email address'),
});

export function ForgotPasswordForm({
  values,
  onSubmit,
  onBackStep,
  formContext,
  backStepLabel,
  label,
  placeholder,
  instructions,
  className,
  ...props
}: ForgotPasswordFormProps): JSX.Element {
  return (
    <Styled className={classNames('ForgotPasswordForm', className)} {...props}>
      <Form
        formik={{
          initialValues: values,
          validateOnChange: false,
          validateOnBlur: false,
          validationSchema,
          onSubmit,
        }}
        formContext={formContext}
      >
        <div>
          {!!instructions && (
            <div className="forgot-password-modal__instructions">
              <p>{instructions}</p>
            </div>
          )}
          <FormItem name="email" label={label}>
            <Input type="email" placeholder={placeholder} />
          </FormItem>
        </div>
        {!!backStepLabel && (
          <div className="forgot-password__back">
            <SimpleLink
              type={1}
              text={backStepLabel}
              href="/login"
              onClick={(event) => {
                event.preventDefault();
                onBackStep();
              }}
            />
          </div>
        )}
      </Form>
    </Styled>
  );
}
ForgotPasswordForm.displayName = 'ForgotPasswordForm';
